<h2 mat-dialog-title class="dialog-title">
  <mat-icon>group_add</mat-icon>
  Opret instruktørinvitation
  <span class="step-indicator">Trin {{ step }} af 2</span>
</h2>

<mat-dialog-content class="dialog-content">
  <!-- ── Step 1: confirm selected events ── -->
  @if (step === 1) {
  <p class="section-intro">
    Du er ved at oprette en instruktørinvitation for følgende events:
  </p>
  <div class="event-list-box">
    @for (ev of selectedEvents; track ev) {
    <div class="event-row">
      <mat-icon class="event-icon">event</mat-icon>
      <div class="event-info">
        <span class="event-name">{{ ev.event_name || ev.guid }}</span>
        <span class="event-meta">
          {{ formatEventDate(ev) }}
          @if (ev.location) { &mdash; {{ ev.location }}
          }
        </span>
      </div>
    </div>
    } @if (selectedEvents.length === 0) {
    <p class="muted">{{ eventGuids.length }} event(s) valgt.</p>
    }
  </div>
  <p class="hint-text">
    <mat-icon class="hint-icon">info_outline</mat-icon>
    Klik på <strong>Næste</strong> for at vælge instruktører og sætte svarfrist.
  </p>
  }

  <!-- ── Step 2: add invitees + deadline ── -->
  @if (step === 2) {
  <!-- ── Search controls ── -->
  <p class="section-label">Søg og tilføj instruktører</p>
  <div class="filters-row">
    <!-- Educational path filter (searchable autocomplete) -->
    <mat-form-field appearance="outline" class="path-filter-field">
      <mat-label>Uddannelsessti</mat-label>
      <mat-icon matPrefix class="path-prefix-icon">school</mat-icon>
      <input
        matInput
        [formControl]="pathFilterControl"
        [matAutocomplete]="pathAuto"
        placeholder="Søg eller vælg uddannelsessti…"
        autocomplete="off"
      />
      @if (selectedPathId) {
      <button
        matSuffix
        mat-icon-button
        (click)="clearPathFilter()"
        matTooltip="Ryd filter"
        type="button"
        class="clear-path-btn"
      >
        <mat-icon>close</mat-icon>
      </button>
      } @if (loadingPaths) {
      <mat-spinner matSuffix diameter="16"></mat-spinner>
      }
      <mat-autocomplete
        #pathAuto="matAutocomplete"
        (optionSelected)="onPathSelected($event.option.value)"
      >
        <mat-option [value]="null" class="all-paths-option">
          <mat-icon class="all-icon">public</mat-icon>
          Alle uddannelsesveje
        </mat-option>
        @for (p of filteredPaths; track p) {
        <mat-option [value]="p">
          {{ p.name }}
        </mat-option>
        }
      </mat-autocomplete>
    </mat-form-field>
    <!-- Name / email search -->
    <mat-form-field appearance="outline" class="name-search-field">
      <mat-label>Søg på navn eller e-mail</mat-label>
      <input
        matInput
        [ngModel]="searchQuery"
        (ngModelChange)="onSearchInput($event)"
        autocomplete="off"
        placeholder="fx. Jensen"
      />
      @if (!searching) {
      <mat-icon matSuffix>search</mat-icon>
      } @if (searching) {
      <mat-spinner matSuffix diameter="18"></mat-spinner>
      }
    </mat-form-field>
  </div>
  <!-- ── Results table ── -->
  <div class="results-container">
    <!-- Empty / hint state -->
    @if (!searchPerformed && !searching) {
    <div class="search-hint">
      <mat-icon>person_search</mat-icon>
      <span
        >Søg på navn eller vælg en uddannelsessti for at se instruktører.</span
      >
    </div>
    }
    <!-- Searching spinner (first load) -->
    @if (searching && searchResults.length === 0) {
    <div class="searching-state">
      <mat-spinner diameter="28"></mat-spinner>
      <span>Søger…</span>
    </div>
    }
    <!-- No results -->
    @if (searchPerformed && !searching && searchResults.length === 0) {
    <div class="no-results-state">
      <mat-icon>search_off</mat-icon>
      <span>Ingen resultater fundet.</span>
    </div>
    }
    <!-- Results table -->
    @if (searchResults.length > 0) {
    <div class="table-wrapper">
      <!-- Selection action bar -->
      <div
        class="selection-bar"
        [class.selection-bar--active]="selectedCount > 0"
      >
        @if (selectedCount > 0) {
        <span class="selection-info">
          <mat-icon>check_circle</mat-icon>
          {{ selectedCount }} person{{ selectedCount !== 1 ? "er" : "" }} valgt
        </span>
        } @if (selectedCount === 0) {
        <span class="selection-info muted">
          {{ searchResults.length }} resultat{{
            searchResults.length !== 1 ? "er" : ""
          }}
        </span>
        }
        <span class="spacer"></span>
        @if (selectedCount > 0) {
        <button
          mat-raised-button
          color="primary"
          class="add-btn"
          (click)="addSelectedToInvitees()"
        >
          <mat-icon>person_add</mat-icon>
          Tilføj valgte
        </button>
        } @if (selectedCount > 0) {
        <button mat-button class="clear-btn" (click)="selection.clear()">
          Ryd valg
        </button>
        }
      </div>
      <table mat-table [dataSource]="searchResults" class="results-table">
        <!-- Select column -->
        <ng-container matColumnDef="select">
          <th mat-header-cell *matHeaderCellDef class="col-select">
            <mat-checkbox
              [checked]="isAllSelected()"
              [indeterminate]="isSomeSelected()"
              (change)="toggleAll()"
              [matTooltip]="isAllSelected() ? 'Fravælg alle' : 'Vælg alle'"
            >
            </mat-checkbox>
          </th>
          <td mat-cell *matCellDef="let user" class="col-select">
            <mat-checkbox
              [checked]="selection.isSelected(user)"
              (click)="$event.stopPropagation()"
              (change)="toggleRow(user)"
            >
            </mat-checkbox>
          </td>
        </ng-container>
        <!-- Name column -->
        <ng-container matColumnDef="name">
          <th mat-header-cell *matHeaderCellDef>Navn</th>
          <td mat-cell *matCellDef="let user" class="col-name">
            <span class="user-name">{{ displayName(user) }}</span>
          </td>
        </ng-container>
        <!-- Email column -->
        <ng-container matColumnDef="email">
          <th mat-header-cell *matHeaderCellDef>E-mail</th>
          <td mat-cell *matCellDef="let user" class="col-email">
            {{ user.email }}
          </td>
        </ng-container>
        <!-- Paths column -->
        <ng-container matColumnDef="paths">
          <th mat-header-cell *matHeaderCellDef>Uddannelsesveje</th>
          <td mat-cell *matCellDef="let user" class="col-paths">
            @if (user.educational_paths?.length) { @for (p of
            user.educational_paths; track p; let i = $index) {
            <span
              class="path-chip"
              [style.background-color]="pathChipColor(p.path_id)"
              [matTooltip]="p.name"
            >
              {{ p.name.length > 6 ? p.name.substring(0, 5) + "…" : p.name }}
            </span>
            } } @else {
            <span class="no-paths-label">Ingen</span>
            }
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns"
          (click)="toggleRow(row)"
          [class.row-selected]="selection.isSelected(row)"
          class="result-row"
        ></tr>
      </table>
    </div>
    }
  </div>
  <mat-divider class="section-divider"></mat-divider>
  <!-- ── Invitees chips ── -->
  <p class="section-label">
    Invitationsliste
    <span class="count-badge">{{ invitees.length }}</span>
  </p>
  @if (invitees.length > 0) {
  <div class="chips-container">
    <mat-chip-set>
      @for (u of invitees; track u) {
      <mat-chip (removed)="removeInvitee(u)" class="invitee-chip">
        <mat-icon matChipAvatar>person</mat-icon>
        {{ displayName(u) }}
        <button matChipRemove aria-label="Fjern">
          <mat-icon>cancel</mat-icon>
        </button>
      </mat-chip>
      }
    </mat-chip-set>
  </div>
  } @if (invitees.length === 0) {
  <p class="muted invitee-empty">
    <mat-icon>people_outline</mat-icon>
    Ingen inviterede tilføjet endnu.
  </p>
  }
  <mat-divider class="section-divider"></mat-divider>
  <!-- ── Deadline ── -->
  <p class="section-label">Svarfrist</p>
  <mat-form-field appearance="outline" class="deadline-field">
    <mat-label>Vælg svarfrist</mat-label>
    <input
      matInput
      [matDatepicker]="picker"
      [(ngModel)]="responseDeadline"
      [min]="minDate"
      readonly
    />
    <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
    <mat-datepicker #picker></mat-datepicker>
    <mat-hint>Instruktørerne skal svare inden denne dato.</mat-hint>
  </mat-form-field>
  @if (error) {
  <div class="error-box">
    <mat-icon>error_outline</mat-icon>
    {{ error }}
  </div>
  } }
</mat-dialog-content>

<mat-dialog-actions align="end" class="dialog-actions">
  @if (step === 1) {
  <button mat-button (click)="close()">Annuller</button>
  <button mat-raised-button color="primary" (click)="nextStep()">
    Næste <mat-icon iconPositionEnd>arrow_forward</mat-icon>
  </button>
  } @if (step === 2) {
  <button mat-button (click)="backStep()">
    <mat-icon>arrow_back</mat-icon> Tilbage
  </button>
  <button mat-button (click)="close()">Annuller</button>
  <button
    mat-raised-button
    color="primary"
    (click)="sendInvitation()"
    [disabled]="sending"
  >
    @if (sending) {
    <mat-spinner diameter="18" class="btn-spinner"></mat-spinner>
    } @if (!sending) {
    <mat-icon>send</mat-icon>
    }
    {{ sending ? "Sender…" : "Send invitation" }}
  </button>
  }
</mat-dialog-actions>
